import { Company } from '@/api/model/company'
import { BaseModel } from '@/api/base-model'
import { ContactAPI } from '@/api/backend-client'
import { Referral } from '@/api/model/referral'
import { EventLog } from '@/api/model/event-log'
import { availableGenders, Gender } from '@/api/types'
import { Source } from '@/api/model/source'

export class Contact extends BaseModel<Contact> {
    protected collectionName = ContactAPI.collectionName

    constructor(
        public company: Company|null,
        public id: string,
        public relatieCode: string|null,
        public source: string,
        public newSource: Source|null,
        public gender: string|null,
        public initials: string|null,
        public firstname: string|null,
        public middlename: string|null,
        public lastname: string,
        public streetname: string|null,
        public streetnumber: string|null,
        public streetnumbersuffix: string|null,
        public zipcode: string|null,
        public city: string|null,
        public country: string|null,
        public latitude: number|null,
        public longitude: number|null,
        public birthDate: Date|null,
        public email: string|null,
        public mobilePhonenumber: string|null,
        public phonenumber: string|null,
        public spouse: boolean,
        public spouseGender: string|null,
        public spouseInitials: string|null,
        public spouseFirstname: string|null,
        public spouseMiddlename: string|null,
        public spouseLastname: string|null,
        public spouseHasCustomAddress: boolean,
        public spouseStreetname: string|null,
        public spouseStreetnumber: string|null,
        public spouseStreetnumbersuffix: string|null,
        public spouseZipcode: string|null,
        public spouseCity: string|null,
        public spouseCountry: string|null,
        public spouseLatitude: string|null,
        public spouseLongitude: string|null,
        public spouseBirthDate: Date|null,
        public spouseHasCustomContactDetails: boolean,
        public spouseEmail: string|null,
        public spouseMobilePhonenumber: string|null,
        public spousePhonenumber: string|null,
        public referrals: Referral[],
        public eventLogs: EventLog[],
        public createdAt: Date,
        public updatedAt: Date,
    ) { super(id) }

    get name(): string {
        return [this.firstname || this.initials, this.middlename, this.lastname]
            .filter(value => value !== null)
            .join(' ')
            .replaceAll(/\s+/g, ' ') // strips additional spaces inside one of the properties
            .trim()
    }

    get spouseName(): string {
        return [this.spouseFirstname || this.spouseInitials, this.spouseMiddlename, this.spouseLastname]
            .filter(value => value !== null)
            .join(' ')
            .replaceAll(/\s+/g, ' ') // strips additional spaces inside one of the properties
            .trim()
    }

    get title(): string {
        if (this.gender === Gender.MALE) {
            return 'dhr.'
        }

        if (this.gender === Gender.FEMALE) {
            return 'mevr.'
        }

        return ''
    }

    /**
     * Informal variant of title.
     */
    get titleInformal(): string {
        if (this.gender === Gender.MALE) {
            return 'heer'
        }

        if (this.gender === Gender.FEMALE) {
            return 'mevrouw'
        }

        return 'heer/mevrouw'
    }

    /**
     * A friendly name, for use in forms. Will attempt to address a contact by firstname
     * If that fails, it will fall back to a more formal way.
     */
    get displayName(): string {
        if (!this.firstname || this.firstname.length === 0) {
            return `${this.titleInformal} ${this.name}`
        }

        return this.firstname
    }

    get spouseTitle(): string {
        if (this.spouseGender === Gender.MALE) {
            return 'dhr.'
        }

        if (this.spouseGender === Gender.FEMALE) {
            return 'mevr.'
        }

        return ''
    }

    get address(): string {
        let address = ''

        if (this.streetname && this.streetname.length > 0) {
            address = address.trim()
            address += ` ${this.streetname}`
        }

        if (this.streetnumber && this.streetnumber.length > 0) {
            address = address.trim()
            address += ` ${this.streetnumber}`
        }

        if (this.streetnumbersuffix && this.streetnumbersuffix.length > 0) {
            address = address.trim()
            address += ` ${this.streetnumbersuffix}`
        }

        if (this.city && this.city.length > 0) {
            address = address.trim()
            address += `, ${this.city}`
        }

        return address
    }

    static fromData(data: any): Contact {
        if (data === null || typeof data === 'string' || data instanceof Contact) {
            return data
        }

        return new Contact(
            data.company ? Company.fromData(data.company) : null,
            data.id,
            data.relatieCode,
            data.source,
            data.newSource ? Source.fromData(data.newSource) : null,
            data.gender,
            data.initials,
            data.firstname,
            data.middlename,
            data.lastname,
            data.streetname,
            data.streetnumber,
            data.streetnumbersuffix,
            data.zipcode,
            data.city,
            data.country,
            data.latitude,
            data.longitude,
            data.birthDate,
            data.email,
            data.mobilePhonenumber,
            data.phonenumber,
            data.spouse || false,
            data.spouseGender,
            data.spouseInitials,
            data.spouseFirstname,
            data.spouseMiddlename,
            data.spouseLastname,
            data.spouseHasCustomAddress || false,
            data.spouseStreetname,
            data.spouseStreetnumber,
            data.spouseStreetnumbersuffix,
            data.spouseZipcode,
            data.spouseCity,
            data.spouseCountry,
            data.spouseLatitude,
            data.spouseLongitude,
            data.spouseBirthDate,
            data.spouseHasCustomContactDetails || false,
            data.spouseEmail,
            data.spouseMobilePhonenumber,
            data.spousePhonenumber,
            Object.values(data.referrals || {}).map(entry => Referral.fromData(entry)),
            Object.values(data.eventLogs || {}).map(entry => EventLog.fromData(entry)),
            data.createdAt,
            data.updatedAt,
        )
    }

    static get validGenders(): string[] {
        return availableGenders()
    }
}
